import { QRCodeSVG } from "qrcode.react";
import { Tv, Eye, EyeOff, Wifi, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LoginScreen() {
  const [showPass, setShowPass] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrStatus, setQrStatus] = useState("loading"); // loading | active | expired
  const qrLoginInProgress = useRef(false);

  const { login, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get("return");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnPath || "/app", { replace: true });
    }
  }, [isAuthenticated, navigate, returnPath]);

  // QR login session: create + poll
  useEffect(() => {
    if (isAuthenticated) return;
    let cancelled = false;
    let pollTimer = null;

    const createSession = async () => {
      setQrStatus("loading");
      try {
        const { data } = await axios.post(`${BACKEND}/api/qr/create`);
        if (cancelled) return;
        setQrCode(data.code);
        setQrStatus("active");
        poll(data.code);
      } catch {
        if (!cancelled) setQrStatus("expired");
      }
    };

    const poll = (code) => {
      pollTimer = setInterval(async () => {
        try {
          const { data } = await axios.get(`${BACKEND}/api/qr/${code}/status`);
          if (cancelled) return;
          if (data.status === "approved" && data.auth_token && !qrLoginInProgress.current) {
            qrLoginInProgress.current = true;
            clearInterval(pollTimer);
            const result = await loginWithToken(data.auth_token);
            if (result.success) {
              navigate("/app", { replace: true });
            } else {
              qrLoginInProgress.current = false;
              createSession();
            }
          } else if (data.status === "expired") {
            clearInterval(pollTimer);
            createSession();
          }
        } catch { /* keep polling */ }
      }, 2500);
    };

    createSession();
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!username.trim() || !password.trim()) {
      setError("İstifadəçi adı və şifrəni daxil edin");
      return;
    }

    setLoading(true);
    
    try {
      const result = await login(username, password);
      
      if (result.success) {
        navigate(returnPath || "/app", { replace: true });
      } else {
        // Translate API error messages to Azerbaijani
        const raw = (result.error || "").toLowerCase();
        console.log("Login error raw:", result.error); // DEBUG
        if (raw.includes("failed to auth") || raw.includes("invalid") || raw.includes("credentials") || raw.includes("401") || raw.includes("403")) {
          setError("İstifadəçi adı və ya şifrə yanlışdır");
        } else if (raw.includes("failed to fetch") || raw.includes("network") || raw.includes("timeout")) {
          setError("İnternet bağlantısı yoxdur. Yenidən cəhd edin");
        } else if (raw.includes("blocked") || raw.includes("suspended") || raw.includes("disabled")) {
          setError("Hesabınız bloklanıb. Dəstək ilə əlaqə saxlayın");
        } else {
          setError("Giriş uğursuz oldu. Yenidən cəhd edin");
        }
      }
    } catch (err) {
      setError("Xəta baş verdi. Yenidən cəhd edin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#09090b]">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1574375927938-d5a98e8d6f18?q=80&w=2000&auto=format&fit=crop)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/90 to-[#09090b]/60" />

      {/* Content */}
      <div className="relative z-10 flex flex-col md:flex-row min-h-screen">
        {/* Left side — Login Form */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-24 py-10 md:py-0 max-w-2xl w-full mx-auto md:mx-0">
          {/* Brand */}
          <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-12 animate-fade-in">
            <img src="/fibernet-logo.png" alt="Fibernet TV" className="w-14 h-14 sm:w-20 sm:h-20 object-contain" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Fibernet TV
              </h1>
              <p className="text-xs sm:text-sm text-zinc-500">Premium IPTV Xidməti</p>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2 animate-fade-in animate-delay-1">
            Xoş gəlmisiniz
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mb-6 sm:mb-8 animate-fade-in animate-delay-1">
            Hesabınıza daxil olun və ya mobil app-la QR kodu scan edin
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 animate-fade-in animate-delay-2">
            {error && (
              <div className="p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                İstifadəçi adı
              </label>
              <input
                data-testid="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="İstifadəçi adınızı daxil edin"
                className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-base sm:text-lg placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Şifrə
              </label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifrənizi daxil edin"
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-base sm:text-lg placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors pr-12 sm:pr-14"
                  disabled={loading}
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white text-base sm:text-lg font-semibold rounded-xl transition-colors focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09090b] flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Gözləyin...
                </>
              ) : (
                "Daxil ol"
              )}
            </button>
          </form>

          {/* Language selector */}
          <div className="flex items-center gap-4 mt-6 sm:mt-8 animate-fade-in animate-delay-3">
            <Wifi className="w-4 h-4 text-zinc-600" />
            <div className="flex gap-2">
              <button className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600/20 text-blue-400 border border-blue-500/30">
                AZ
              </button>
              <button className="px-3 py-1.5 text-sm font-medium rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors">
                RU
              </button>
              <button className="px-3 py-1.5 text-sm font-medium rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors">
                EN
              </button>
            </div>
          </div>

          {/* Mobile-only forgot/register links (since QR panel is hidden on mobile) */}
          <div className="md:hidden flex items-center justify-center gap-4 mt-8 text-sm">
            <button
              type="button"
              className="text-zinc-400 hover:text-blue-400 transition-colors"
              onClick={() => alert("Şifrə bərpası funksiyası tezliklə əlavə olunacaq")}
            >
              Şifrəmi unutdum
            </button>
            <span className="text-zinc-700">|</span>
            <button
              type="button"
              className="text-zinc-400 hover:text-blue-400 transition-colors"
              onClick={() => alert("Qeydiyyat funksiyası tezliklə əlavə olunacaq")}
            >
              Qeydiyyat
            </button>
          </div>
        </div>

        {/* Right side — QR Code (hidden on mobile) */}
        <div className="hidden md:flex flex-1 flex-col items-center justify-center">
          <div className="text-center animate-fade-in animate-delay-2">
            <div className="inline-block p-8 bg-white rounded-2xl qr-glow mb-6 relative" data-testid="qr-login-box">
              {qrCode ? (
                <QRCodeSVG
                  value={`${window.location.origin}/qr-approve/${qrCode}`}
                  size={220}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#09090b"
                />
              ) : (
                <div className="w-[220px] h-[220px] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />
                </div>
              )}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              QR ilə giriş
            </h3>
            <p className="text-zinc-400 max-w-xs mx-auto leading-relaxed">
              Mobil <span className="text-blue-400 font-medium">Fibernet TV</span> tətbiqində
              Profil → <span className="text-blue-400 font-medium">QR oxut</span> düyməsi ilə kodu oxudun
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-zinc-600 text-sm" data-testid="qr-login-status">
              <div className={`w-2 h-2 rounded-full ${qrStatus === "active" ? "bg-emerald-500 live-dot" : "bg-zinc-600"}`} />
              {qrStatus === "active" ? "Gözlənilir..." : qrStatus === "loading" ? "Kod yaradılır..." : "Yenilənir..."}
            </div>
            
            {/* Forgot password & Registration links */}
            <div className="flex items-center justify-center gap-6 mt-12 text-sm">
              <button
                type="button"
                data-testid="forgot-password-link"
                className="text-zinc-400 hover:text-blue-400 transition-colors"
                onClick={() => alert("Şifrə bərpası funksiyası tezliklə əlavə olunacaq")}
              >
                Şifrəmi unutdum
              </button>
              <span className="text-zinc-700">|</span>
              <button
                type="button"
                data-testid="register-link"
                className="text-zinc-400 hover:text-blue-400 transition-colors"
                onClick={() => alert("Qeydiyyat funksiyası tezliklə əlavə olunacaq")}
              >
                Qeydiyyat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
